import { ROLES } from '@/types/auth';
import type { RoleType } from '@/types/auth';

/**
 * Wire-format role names → the app's canonical `RoleType` vocabulary.
 *
 * The backend has shipped two spellings for the same three roles: the original
 * `SystemAdmin`/`Wholesaler`/`Billing` and the newer `globalAdmin`/`admin`/`billingAdmin`
 * (observed on the team endpoints). Both are still in circulation across `GET /me`,
 * `GET /me/workspaces` and `GET /wholesalers/{id}/team`, and the two differ in casing as
 * well as wording — so every wire value is funnelled through here, lower-cased, rather than
 * compared against a literal. Comparing raw strings is what silently demoted Global Admins
 * to no role at all.
 */
const ROLE_ALIASES: Record<string, RoleType> = {
  globaladmin: ROLES.globalAdmin,
  systemadmin: ROLES.globalAdmin,
  admin: ROLES.admin,
  wholesaler: ROLES.admin,
  billingadmin: ROLES.billingAdmin,
  billing: ROLES.billingAdmin,
};

/**
 * Returns null for role strings we don't recognise. Callers decide how strict to be —
 * client-side gating is UX-only, so most read null as "don't hide anything" while the
 * Global Admin gate reads it as "deny".
 */
export const normalizeRole = (role: string | null | undefined): RoleType | null => {
  if (role == null) {
    return null;
  }

  return ROLE_ALIASES[role.trim().toLowerCase()] ?? null;
};

/** Normalise a list of wire role names, dropping unrecognised entries and de-duplicating. */
export const normalizeRoles = (roles: string[]): RoleType[] => {
  const mapped = roles
    .map((role) => normalizeRole(role))
    .filter((role): role is RoleType => role != null);

  return Array.from(new Set(mapped));
};
